/**
 * ChangesController — /api/v1/changes
 * Mirrors: src/Aidepo.Api/Controllers/ChangesController.cs
 *
 * Universal change journal + undo (Phase 1.5.3). Every mutation — human or AI —
 * goes through MutationService and produces a ChangeRecord; this client lists
 * them and undoes them. Replaces the old AgentActions stub.
 */
import { apiGet, apiPost } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

/** Discriminator of a ChangeRecord. */
export type ChangeKind = 'create' | 'move' | 'update' | 'delete' | 'destruct';

/** Who initiated the change. */
export type ActorKind = 'user' | 'ai';

export interface ChangeRecordRow {
  readonly id: Uuid;
  readonly kind: ChangeKind;
  readonly actorKind: ActorKind;
  readonly createdAt: IsoDateTime;
  readonly expiresAt: IsoDateTime;
  readonly undoneAt: IsoDateTime | null;
  /** Подпись затронутой сущности (правило ТО, имя вещи). null у старых записей. */
  readonly entityLabel?: string | null;
  /**
   * Группировочный объект для экрана «История» (aidepo-20). Дорезолвивается
   * на стороне API из snapshot + лукап в БД; для правила ТО — родительское
   * оборудование. null если не удалось определить.
   */
  readonly objectKind?: 'item' | 'room' | 'equipment' | 'property' | 'scheduleRule' | string | null;
  readonly objectId?: Uuid | null;
  readonly objectLabel?: string | null;
  /** Raw reversible snapshot — JSON string, shape depends on `kind`. */
  readonly snapshotJson: string;
}

export interface ListChangesResponse {
  readonly count: number;
  readonly changes: readonly ChangeRecordRow[];
}

export interface UndoChangeResponse {
  readonly id: Uuid;
  readonly undone: boolean;
}

export interface ListChangesParams {
  /** Lower bound on createdAt (inclusive). */
  readonly since?: IsoDateTime;
  /** Page size, 1..200, backend default 50. */
  readonly limit?: number;
}

/** GET /api/v1/changes — recent ChangeRecords for the UndoFull screen. */
export function listChanges(params?: ListChangesParams): Promise<ListChangesResponse> {
  const qs = new URLSearchParams();
  if (params?.since !== undefined) qs.set('since', params.since);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  const suffix = qs.toString();
  return apiGet<ListChangesResponse>(`/api/v1/changes${suffix.length > 0 ? `?${suffix}` : ''}`);
}

/** GET /api/v1/changes/{id} — a single ChangeRecord. */
export function getChange(id: Uuid): Promise<ChangeRecordRow> {
  return apiGet<ChangeRecordRow>(`/api/v1/changes/${id}`);
}

/**
 * POST /api/v1/changes/{id}/undo — reverse a change.
 * Throws ApiError: 404 not found, 409 drift conflict, 410 expired/already undone.
 */
export function undoChange(id: Uuid): Promise<UndoChangeResponse> {
  return apiPost<UndoChangeResponse>(`/api/v1/changes/${id}/undo`);
}
